/*************************************************************/
// NumeroALetras
// The MIT License (MIT)
// 
// Copyright (c) 2015 Luis Alfredo Chee 
// 
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// 
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// 
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
// 
// @author Rodolfo Carmona
// @contributor Jean (jpbadoino@gmail.com)
// @contributor Eliseo Loría (eloria@clavisco.com)
/*************************************************************/

import { Pipe, PipeTransform } from '@angular/core';
import { Constants } from '../common';

@Pipe({
  name: 'numberToWords'
})
export class NumberToWordsPipe implements PipeTransform {
  Data: {
    numero: number,
    enteros: number,
    centavos: number,
    letrasCentimos: string,
    letrasMonedaPlural: string,
    letrasMonedaSingular: string,
    letrasMonedaCentimosPlural: string,
    letrasMonedaCentimoSingular: string
  }

  transform(value: number | string, args: {[id: string]: string} = {}): string {
    if(value === '') return '';

    let num = parseFloat(value.toString());

    const CURRENCY: string = args['currency'] ?? '';

    this.Data = {
      numero: num,
      enteros: Math.floor(num),
      centavos: (((Math.round(num * 100))-(Math.floor(num) * 100))),
      letrasCentimos: '',
      letrasMonedaPlural: CURRENCY, //'PESOS', 'Dólares', 'Bolívares', 'etcs'
      letrasMonedaSingular: CURRENCY.replace('ES', ''), //'PESO', 'Dólar', 'Bolivar', 'etc'
      letrasMonedaCentimosPlural: (CURRENCY === Constants.COLONS) ? ' CENTIMOS' : ((CURRENCY === Constants.DOLARS) ? ' CENTAVOS' : ''), //'CENTIMOS',
      letrasMonedaCentimoSingular: (CURRENCY === Constants.COLONS) ? ' CENTIMO' : ((CURRENCY === Constants.DOLARS) ? ' CENTAVO' : '') //'CENTIMO'
    }

    //Cuando los número llevan delante ceros
    var split_num = (value+"").split('.');
    var ceros_antes = "";
    if(split_num.length >= 1){
      if(split_num[0] != "0"){
          let ceros = split_num[0].replace(this.Data.enteros+"", "");
          for(let cero of ceros)
              ceros_antes += 'CERO '
      }
    }

    //Ceros antes en la coma decimal
    var ceros_despues = ""
    if(split_num.length >= 2){
      if(split_num[1] != "0" && this.Data.centavos < 10){
          for(let comma of split_num[1].replace(this.Data.centavos+"", ""))
              ceros_despues += 'CERO '
      }
    }

    if (this.Data.centavos > 0) {
      this.Data.letrasCentimos = (args['currency'] ? ' CON ' : ' PUNTO ' + ceros_despues) + (() => {
        if (this.Data.centavos == 1)
            return this.Millones(this.Data.centavos, args['currency']) + this.Data.letrasMonedaCentimoSingular;
        else
            return this.Millones(this.Data.centavos, args['currency']) + this.Data.letrasMonedaCentimosPlural;
      })();
    };

    if(this.Data.enteros == 0)
        return ceros_antes + 'CERO' + (args['currency'] ? ' ' : '') + this.Data.letrasMonedaPlural + this.Data.letrasCentimos;
    
    if (this.Data.enteros == 1 && args['type'] === 'single')
        return ceros_antes + 'UNO' + this.Data.letrasCentimos;

    if (this.Data.enteros == 1 && args['type'] === 'date')
        return ceros_antes + 'PRIMERO' + this.Data.letrasCentimos;

    if (this.Data.enteros == 1)
        return ceros_antes + (args['currency'] ? 'UN ' : 'UNO') + this.Data.letrasMonedaSingular + this.Data.letrasCentimos;
    else
        return ceros_antes + this.Millones(this.Data.enteros, args['currency']).trim() + (args['currency'] ? ' ' : '') + this.Data.letrasMonedaPlural + this.Data.letrasCentimos;
  }

  private Unidades(num){
    switch(num)
    {
        case 1: return 'UNO';
        case 2: return 'DOS';
        case 3: return 'TRES';
        case 4: return 'CUATRO';
        case 5: return 'CINCO';
        case 6: return 'SEIS';
        case 7: return 'SIETE';
        case 8: return 'OCHO';
        case 9: return 'NUEVE';
        default: return '';
    }
  }

  private Decenas(num){

    let decena = Math.floor(num/10);
    let unidad = num - (decena * 10);

    switch(decena)
    {
        case 1:
            switch(unidad)
            {
                case 0: return 'DIEZ';
                case 1: return 'ONCE';
                case 2: return 'DOCE';
                case 3: return 'TRECE';
                case 4: return 'CATORCE';
                case 5: return 'QUINCE';
                default:
                  // Se define como una excepcion por la tilde
                  let decena = 'DIECI' + this.Unidades(unidad).trim();
                  if(decena.toUpperCase() === 'DIECISEIS') 
                    decena = 'DIECISÉIS';
                  return decena;
            }
        case 2:
            switch(unidad)
            {
                case 0: return 'VEINTE';
                default: 
                  // Se definen como excepcion por la tilde
                  let veintena = 'VEINTI' + this.Unidades(unidad).trim();
                  if(veintena.toUpperCase() === 'VEINTIDOS') 
                    veintena = 'VEINTIDÓS';
                  else if(veintena.toUpperCase() === 'VEINTITRES') 
                    veintena = 'VEINTITRÉS';
                  else if(veintena.toUpperCase() === 'VEINTISEIS') 
                    veintena = 'VEINTISÉIS';
                  return veintena;
            }
        case 3: return this.DecenasY('TREINTA', unidad).trim();
        case 4: return this.DecenasY('CUARENTA', unidad).trim();
        case 5: return this.DecenasY('CINCUENTA', unidad).trim();
        case 6: return this.DecenasY('SESENTA', unidad).trim();
        case 7: return this.DecenasY('SETENTA', unidad).trim();
        case 8: return this.DecenasY('OCHENTA', unidad).trim();
        case 9: return this.DecenasY('NOVENTA', unidad).trim();
        case 0: return this.Unidades(unidad).trim();
    }
  }

  private DecenasY(strSin, numUnidades) {
    if (numUnidades > 0)
    return strSin + ' Y ' + this.Unidades(numUnidades).trim()

    return strSin;
  }

  private Centenas(num) {
    let centenas = Math.floor(num / 100);
    let decenas = num - (centenas * 100);

    switch(centenas)
    {
        case 1:
            if (decenas > 0)
                return 'CIENTO ' + this.Decenas(decenas).trim();
            return 'CIEN';
        case 2: return 'DOSCIENTOS ' + this.Decenas(decenas).trim();
        case 3: return 'TRESCIENTOS ' + this.Decenas(decenas).trim();
        case 4: return 'CUATROCIENTOS ' + this.Decenas(decenas).trim();
        case 5: return 'QUINIENTOS ' + this.Decenas(decenas).trim();
        case 6: return 'SEISCIENTOS ' + this.Decenas(decenas).trim();
        case 7: return 'SETECIENTOS ' + this.Decenas(decenas).trim();
        case 8: return 'OCHOCIENTOS ' + this.Decenas(decenas).trim();
        case 9: return 'NOVECIENTOS ' + this.Decenas(decenas).trim();
    }
    return this.Decenas(decenas).trim();
  }

  private Seccion(num, divisor, strSingular, strPlural) {
    let cientos = Math.floor(num / divisor)
    let resto = num - (cientos * divisor)

    let letras = '';

    if (cientos > 0)
        if (cientos > 1)
            letras = this.Centenas(cientos) + ' ' + strPlural;
        else
            letras = strSingular;

    if (resto > 0)
      letras += '';
    
    return letras;
  }

  private Miles(num) {
    let divisor = 1000;
    let cientos = Math.floor(num / divisor)
    let resto = num-(cientos * divisor)

    let strMiles = this.Seccion(num, divisor, 'MIL', 'MIL');
    let strCentenas = this.Centenas(resto).trim();

    // Correccion para números como 101000, 291070, etc 
    // que se escriben como Y UN MIL en lugar de Y UNO MIL
    if(cientos > 20 && strMiles.endsWith('Y UNO MIL')){
      strMiles = strMiles.slice(0, strMiles.length-5) + strMiles.slice(strMiles.length-4, strMiles.length);
    }

    if(strMiles == '')
        return strCentenas;

    return strMiles + ' ' + strCentenas;
  }

  private Millones(num, currency) {
    let divisor = 1000000;
    let cientos = Math.floor(num / divisor)
    let resto = num-(cientos * divisor)

    let strMillones = this.Seccion(num, divisor, 'UN MILLON', 'MILLONES');
    let strMiles = this.Miles(resto).trim();

    if(strMillones == '')
        return strMiles;

    if(resto === 0 && currency)
      strMillones += ' DE';

    return strMillones + ' ' + strMiles;
  }

}
